import { Languages, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  STORAGE_KEY,
  ensureLanguage,
  isLocalizedPublicPath,
  stripLangPrefix,
  withLangPrefix,
} from '../lib/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LANGS: Array<{ code: 'en' | 'uk'; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'uk', label: 'Українська' },
];

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const current = i18n.resolvedLanguage ?? 'en';

  // Remember the choice so the app (unprefixed routes) honors it via the
  // detector; the detector no longer auto-caches (see i18n.ts).
  const selectLang = async (code: 'en' | 'uk') => {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* private mode / storage disabled — language still applies for this view */
    }
    const { pathname, search, hash } = window.location;
    if (isLocalizedPublicPath(pathname)) {
      // The URL carries the language here: navigate (full reload) to the
      // localized path so the per-language prerendered page is served.
      const target = withLangPrefix(stripLangPrefix(pathname), code);
      window.location.href = `${target}${search}${hash}`;
    } else {
      // uk is code-split — load its bundle before switching so the app doesn't
      // flash English keys while the chunk downloads.
      await ensureLanguage(code);
      i18n.changeLanguage(code);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <Languages className="h-[1.2rem] w-[1.2rem]" />
          <span className="sr-only">{t('language.toggle')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-max">
        {LANGS.map(({ code, label }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => selectLang(code)}
            className="flex items-center justify-between"
          >
            <span>{label}</span>
            {current === code && <Check className="h-4 w-4 ml-2" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
